import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Archive, Pencil, Plus, ReceiptText, X } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { PrecioDelServicio } from "@/componentes/catalogos/PrecioDelServicio"
import {
  claveServicios,
  type ServicioOfrecido,
  useServiciosProveedor,
} from "@/componentes/catalogos/serviciosProveedor"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { FormularioGasto, type Gasto } from "@/paginas/interno/Gastos"

type Editando =
  | { que: "agregar" }
  | { que: "propio"; gasto: Gasto | null }
  | { que: "precio"; servicio: ServicioOfrecido }

/** Lo que `FormularioGasto` necesita de un servicio propio para editarlo. */
function comoGasto(s: ServicioOfrecido, proveedorId: number): Gasto {
  return {
    id: s.gasto_id,
    codigo: s.codigo,
    nombre: s.nombre,
    descripcion: null,
    categoria_id: s.categoria_id,
    categoria: s.categoria,
    proveedor_id: proveedorId,
    proveedor: null,
    tipo_gasto: s.tipo_gasto,
    monto_referencia: s.monto_referencia,
    divisa_id: s.divisa_id,
    divisa: s.divisa,
    activo: true,
  }
}

/**
 * «Servicios y precios» en la ficha del proveedor (04/11, pedido del usuario): lo que
 * presta y a qué precio.
 *
 * ⚠ **Los servicios son del TIPO de proveedor**: «Almacenaje» se crea una vez en
 * «Almacenadora» y cada almacenadora MARCA aquí si lo presta, con su precio (decisión del
 * usuario: se marca, no los presta todos solo por ser del tipo). Un proveedor de varios
 * tipos marca de todos. Lo que sólo se le paga a él —el alquiler de la oficina— sigue
 * siendo un servicio PROPIO, con su precio en el servicio.
 *
 * ⚠ **La MISMA respuesta que lee la orden de pago** (`claveServicios`): lo que se marca
 * aquí sale allí al elegir al proveedor, con este precio.
 *
 * ⚠ **Sin poder ver el gasto de la agencia sólo salen los nombres** (`nombres`, del
 * perfil): el precio es lo que paga la agencia. Cambiar pide `gasto:administrar`.
 */
export function ServiciosDelProveedor({
  proveedorId,
  proveedorNombre,
  nombres,
}: {
  proveedorId: number
  proveedorNombre: string
  /** Los nombres que trae el perfil, para quien no puede ver los precios. */
  nombres: string[]
}) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeVer = tienePermiso(usuario, "gasto:ver") || tienePermiso(usuario, "orden_pago:crear")
  const puedeEditar = tienePermiso(usuario, "gasto:administrar")
  const [editando, setEditando] = useState<Editando | null>(null)
  const [quitando, setQuitando] = useState<number | null>(null)
  const consulta = useServiciosProveedor(proveedorId, puedeVer)

  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: claveServicios(proveedorId) })
    clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
    clienteQuery.invalidateQueries({ queryKey: ["proveedor-perfil", proveedorId] })
  }

  const quitar = useMutation({
    mutationFn: (s: ServicioOfrecido) =>
      s.origen === "TIPO"
        ? api.delete(`/api/v1/proveedores/${proveedorId}/servicios/${s.gasto_id}`)
        : api.patch(`/api/v1/gastos/${s.gasto_id}`, { activo: false }),
    onSuccess: () => {
      setQuitando(null)
      refrescar()
    },
  })

  if (!puedeVer) {
    if (nombres.length === 0) return null
    return (
      <section className="space-y-1">
        <h3 className="text-sm font-semibold">Servicios que presta</h3>
        <ul className="flex flex-wrap gap-2">
          {nombres.map((nombre) => (
            <li key={nombre}>
              <Insignia tono="neutro" icono={<ReceiptText size={12} />}>
                {nombre}
              </Insignia>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  const datos = consulta.data
  const tipos = datos?.tipos ?? []
  const deSusTipos = tipos.map((t) => t.nombre).join(", ")

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Servicios y precios</h3>
        {puedeEditar && editando === null && datos && (
          <Boton tamano="sm" onClick={() => setEditando({ que: "agregar" })}>
            <Plus size={14} aria-hidden={true} /> Agregar servicio
          </Boton>
        )}
      </div>

      {editando?.que === "agregar" && datos && (
        <div className="space-y-2">
          {tipos.length > 0 ? (
            <PrecioDelServicio
              proveedorId={proveedorId}
              elegibles={datos.disponibles}
              tipoParaCrear={tipos[0]}
              onListo={() => {
                setEditando(null)
                refrescar()
              }}
            />
          ) : (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              No tiene tipo de proveedor: dale uno con «Editar ficha» para que preste los servicios
              de su tipo.
            </p>
          )}
          <button
            type="button"
            className="text-xs underline"
            onClick={() => setEditando({ que: "propio", gasto: null })}
          >
            O uno que sólo le paga la agencia a {proveedorNombre} (un alquiler, por ejemplo)
          </button>
        </div>
      )}

      {editando?.que === "propio" && (
        <div className="tarjeta p-3">
          <FormularioGasto
            key={editando.gasto?.id ?? "nuevo"}
            gasto={editando.gasto}
            proveedorFijo={{ id: proveedorId, nombre: proveedorNombre }}
            onCerrar={() => setEditando(null)}
            onGuardado={() => {
              setEditando(null)
              refrescar()
            }}
          />
        </div>
      )}

      {consulta.isPending && <EstadoCarga etiqueta="Cargando sus servicios…" filas={2} />}
      {consulta.isError && (
        <EstadoError
          titulo="No se pudieron cargar sus servicios."
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
        />
      )}
      {datos && datos.ofrecidos.length === 0 && editando === null && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {tipos.length > 0
            ? `Todavía no marca ninguno. Agrega los de ${deSusTipos} que presta, con su precio, y saldrán al redactarle una orden de pago.`
            : "Todavía no tiene servicios."}
        </p>
      )}

      {datos && datos.ofrecidos.length > 0 && (
        <ul className="space-y-1">
          {datos.ofrecidos.map((s) => (
            <li key={s.gasto_id} className="space-y-2" data-testid={`servicio-${s.gasto_id}`}>
              <div className="tarjeta flex flex-wrap items-center gap-x-3 gap-y-1 p-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block font-medium break-words">{s.nombre}</span>
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {[
                      s.origen === "TIPO" ? s.tipo_proveedor : "Sólo de este proveedor",
                      s.categoria ?? "Sin categoría",
                    ].join(" · ")}
                  </span>
                </span>
                <span className="text-right">
                  <strong className="block tabular-nums">
                    {s.monto ? `${monto(s.monto)} ${s.divisa ?? ""}` : "Sin precio"}
                  </strong>
                  {s.origen === "TIPO" && s.monto && (
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {s.precio_propio ? "Su precio" : "De referencia"}
                    </span>
                  )}
                </span>
                {puedeEditar &&
                  editando === null &&
                  (quitando === s.gasto_id ? (
                    <span className="flex items-center gap-1 text-xs">
                      {s.origen === "TIPO" ? "¿Ya no lo presta?" : "¿Archivar?"}
                      <Boton
                        tamano="sm"
                        variante="peligro"
                        disabled={quitar.isPending}
                        onClick={() => quitar.mutate(s)}
                      >
                        Sí
                      </Boton>
                      <Boton tamano="sm" onClick={() => setQuitando(null)}>
                        No
                      </Boton>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <button
                        type="button"
                        aria-label={`Cambiar ${s.nombre}`}
                        onClick={() =>
                          setEditando(
                            s.origen === "TIPO"
                              ? { que: "precio", servicio: s }
                              : { que: "propio", gasto: comoGasto(s, proveedorId) },
                          )
                        }
                        className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                      >
                        <Pencil size={14} aria-hidden={true} />
                      </button>
                      <button
                        type="button"
                        aria-label={
                          s.origen === "TIPO" ? `Ya no presta ${s.nombre}` : `Archivar ${s.nombre}`
                        }
                        onClick={() => setQuitando(s.gasto_id)}
                        className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                      >
                        {s.origen === "TIPO" ? (
                          <X size={14} aria-hidden={true} />
                        ) : (
                          <Archive size={14} aria-hidden={true} />
                        )}
                      </button>
                    </span>
                  ))}
              </div>
              {editando?.que === "precio" && editando.servicio.gasto_id === s.gasto_id && (
                <PrecioDelServicio
                  proveedorId={proveedorId}
                  servicio={{
                    gasto_id: s.gasto_id,
                    nombre: s.nombre,
                    tipo_proveedor: s.tipo_proveedor,
                    monto_referencia: s.monto_referencia,
                    divisa: s.divisa_referencia,
                  }}
                  precioActual={
                    s.precio_propio ? { monto: s.monto, divisa_id: s.divisa_id } : undefined
                  }
                  onListo={() => {
                    setEditando(null)
                    refrescar()
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {quitar.error instanceof ErrorApi && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {quitar.error.message}
        </p>
      )}
    </section>
  )
}
